import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { Link } from "react-router-dom";
import type { Lead, LeadStage } from "../../lib/leads";
import { LEAD_STAGES, STAGE_LABELS, stageOf } from "../../lib/leads";

interface LeadCardProps {
  lead: Lead;
  dragging?: boolean; // true when rendered inside the DragOverlay
  // AUD-015 — the non-drag fallback: dragging (mouse OR keyboard) is a
  // single point of failure for this workflow, so every card also offers a
  // plain, always-keyboard-operable <select> that calls the same
  // updateLeadStage path. Omitted on the DragOverlay clone (dragging=true)
  // — that's a visual copy, not a real interactive card.
  onMoveToStage?: (idempotencyKey: string, stage: LeadStage) => void;
}

export function LeadCard({ lead, dragging = false, onMoveToStage }: LeadCardProps) {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, isDragging } =
    useDraggable({ id: lead.idempotencyKey });

  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.3 : 1,
  };

  const submittedDate = new Date(lead.submissionTimestamp).toLocaleDateString(
    "en-US",
    { month: "short", day: "numeric" },
  );
  const locationText = [lead.city, lead.zip].filter(Boolean).join(", ") || null;

  // round9/design-pass (axe `nested-interactive`, serious): dnd-kit's
  // `attributes` make their host `role="button"` + `tabindex="0"`. Spread on
  // the whole card, that turned a card containing a <select> and a <link>
  // into a button with interactive children — assistive tech cannot reliably
  // reach those. So the ACCESSIBLE draggable is now a dedicated grip button
  // (`setActivatorNodeRef` + `attributes`), while the pointer/keyboard
  // `listeners` stay on the card: a mouse still drags from anywhere on the
  // card (dnd-kit's KeyboardSensor only activates when the key event's
  // target IS the activator, so Space on the select still opens the select).
  return (
    <div
      ref={dragging ? undefined : setNodeRef}
      style={dragging ? { boxShadow: "0 14px 30px -18px rgba(11, 40, 70, 0.38)" } : style}
      className="lead-card"
      {...(dragging ? {} : listeners)}
    >
      <div className="lead-card-head">
        <div className="lead-card-name">{lead.homeownerName}</div>
        {!dragging && (
          <button
            type="button"
            ref={setActivatorNodeRef}
            className="lead-card-handle"
            aria-label={`Drag ${lead.homeownerName} to another stage`}
            {...attributes}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false">
              <circle cx="4.5" cy="2.5" r="1.4" fill="currentColor" />
              <circle cx="9.5" cy="2.5" r="1.4" fill="currentColor" />
              <circle cx="4.5" cy="7" r="1.4" fill="currentColor" />
              <circle cx="9.5" cy="7" r="1.4" fill="currentColor" />
              <circle cx="4.5" cy="11.5" r="1.4" fill="currentColor" />
              <circle cx="9.5" cy="11.5" r="1.4" fill="currentColor" />
            </svg>
          </button>
        )}
      </div>
      <div className="lead-card-email">{lead.homeownerEmail}</div>
      {locationText && <div className="lead-card-location">{locationText}</div>}
      <div className="lead-card-footer">
        <span className="lead-card-date">{submittedDate}</span>
        {lead.submissionStatus === "closed" && (
          <span className="lead-card-closed-badge">Closed</span>
        )}
        {lead.source === "portal" && (
          <span className="lead-card-source-badge" title="Added by your team, not submitted through the designer">Team</span>
        )}
      </div>
      {!dragging && onMoveToStage && (
        <select
          className="lead-card-move-select"
          value={stageOf(lead)}
          aria-label={`Move ${lead.homeownerName} to a different stage`}
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onMoveToStage(lead.idempotencyKey, e.target.value as LeadStage)}
        >
          {LEAD_STAGES.map((s) => (
            <option key={s} value={s}>{STAGE_LABELS[s]}</option>
          ))}
        </select>
      )}
      {!dragging && (
        <Link
          to={`/leads/${encodeURIComponent(lead.idempotencyKey)}`}
          className="lead-card-link"
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          View →
        </Link>
      )}
    </div>
  );
}
